import type { SmartXPortfolioStatusDto, SmartXPortfolioStatusDtoStateEnum } from "@eu.mdotm.api/sphere";
import { AutoTooltip, Column, Text, TooltipContent } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { useEventBus } from "$root/event-bus";
import { useUserValue } from "$root/functional-areas/user";
import { objMatchFn } from "$root/utils";
import { portfolioRepository } from "./repository";
import { keyedBlockedPositions } from "./smartx-blocked-positions";

type ShownSmartXState = Exclude<SmartXPortfolioStatusDtoStateEnum, "NOT_ENABLED">;

type StateText = { label: string; color?: string };

// TODO(MDOT-25473): product question — should the status also show `lastSyncDate`, when the last sync to
// the model ended? The card leaves it out.
const stateTextBySmartXState: Record<ShownSmartXState, StateText> = {
	NOT_LINKED: { label: "Not linked", color: themeCSSVars.palette_N500 },
	LINKED: { label: "Linked" },
	SYNCED: { label: "Synced" },
	SYNC_ERROR: { label: "Sync error", color: themeCSSVars.palette_D500 },
	BLOCKED: { label: "Blocked", color: themeCSSVars.palette_W500 },
};

/**
 * The portfolio's SmartX status for the page header, written like the "Sync Active" line: the state in bold,
 * coloured unless it is Linked or Synced, and on hover the reason behind a failed or blocked sync. Renders nothing
 * for a customer without SmartX, without asking for the status, or until the status has been read once; a later
 * read that fails keeps the last status.
 */
export function SmartXStatus({ portfolioUuid }: { portfolioUuid: string }): React.JSX.Element | null {
	const { customerSmartXEnabled } = useUserValue();
	return customerSmartXEnabled ? <SmartXStatusLine portfolioUuid={portfolioUuid} /> : null;
}

function SmartXStatusLine({ portfolioUuid }: { portfolioUuid: string }): React.JSX.Element | null {
	const { data: status, refetch } = portfolioRepository.smartXStatus.useQuery(portfolioUuid);

	useEventBus("smartx-sync", {
		filter: objMatchFn({ uuid: portfolioUuid }),
		listener: () => {
			refetch().catch(noop);
		},
	});
	// A reopened stream does not replay the events it missed while it was down.
	useEventBus("server-events-stream-open", {
		listener: () => {
			refetch().catch(noop);
		},
	});

	if (status?.state == null || status.state === "NOT_ENABLED") {
		return null;
	}

	// Undefined for a state the backend added before this client knew it: show nothing rather than break the page.
	const stateText: StateText | undefined = stateTextBySmartXState[status.state];
	if (stateText == null) {
		return null;
	}
	const { label, color } = stateText;
	const line = (innerRef?: (el: HTMLDivElement | null) => void) => (
		<div
			ref={innerRef}
			className="flex items-center space-x-2 whitespace-nowrap"
			data-qualifier="PortfolioDetails/PageHeader/SmartX"
			data-state={status.state}
		>
			<span>SmartX</span>
			<strong style={{ color }}>{label}</strong>
		</div>
	);

	return status.state === "SYNC_ERROR" || status.state === "BLOCKED" ? (
		<AutoTooltip maxWidth={400} position="bottom" overrideColor={color} trigger={({ innerRef }) => line(innerRef)}>
			<SmartXStatusDetail status={status} />
		</AutoTooltip>
	) : (
		line()
	);
}

function SmartXStatusDetail({ status }: { status: SmartXPortfolioStatusDto }): React.JSX.Element {
	const blocked = status.blockedPositions ?? [];
	return (
		<TooltipContent>
			<Column gap={4}>
				<Text type="Body/M/Bold">{status.state === "BLOCKED" ? "SmartX sync blocked" : "SmartX sync failed"}</Text>
				{status.reason != null && <Text type="Body/M/Book">{status.reason}</Text>}
				{blocked.length > 0 && (
					<ul className="list-disc pl-4">
						{keyedBlockedPositions(blocked).map(({ key, position }) => (
							<li key={key}>
								{position.name}
								{position.alias ? ` (${position.alias})` : ""}
								{position.reason ? `: ${position.reason}` : ""}
							</li>
						))}
					</ul>
				)}
			</Column>
		</TooltipContent>
	);
}
